// MessageThread.tsx — self-contained, embeddable, prop-driven Petcademy Messages
// thread. Components: MediaGroup, MediaLightbox, MessageBubble, DateDivider,
// NewMessageDivider, ResponseTime, DisabledComposer, UpgradePrompt, EmptyState.
//
// Sizes to its parent container (100% / 100%). No viewport units, no fixed
// chrome, no body styles. All colors/fonts come from --pc-* tokens (tokens.css)
// so partners can re-theme via a `className`. Ported from the Claude Design
// prototype; framework-free (no MUI) to stay embeddable.
import React, {
  useRef, useState, useEffect, useLayoutEffect, useCallback,
} from 'react';
import Icon from './Icon';
import Composer, { ComposerApi, ComposerSendPayload } from './Composer';
import { linkifyParts } from './linkify';
import { parseMessageBlocks } from './messageBlocks';
import {
  MembershipStatus, ThreadMessage, TimelineMedia,
} from './types';
import './tokens.css';
import './thread.css';

/* ---------------------------------------------------------------- linkify */
// Host hook for links inside message bubbles. Returns true when the host handled
// the link (e.g. navigated in-app), in which case the default new-tab open is
// prevented. Only plain left-clicks are offered to the host — modified clicks
// (cmd/ctrl/shift/alt: new tab, new window, selection) keep the browser default.
export type LinkClickHandler = (href: string) => boolean;

function maybeHandleLink(e: React.MouseEvent, href: string, onLinkClick?: LinkClickHandler) {
  if (!onLinkClick || !href) return;
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  if (onLinkClick(href)) e.preventDefault();
}

function Linkified({ text, onLinkClick }: { text: string; onLinkClick?: LinkClickHandler }) {
  return (
    <>
      {linkifyParts(text).map((p, i) => (p.type === 'link' ? (
        <a
          /* eslint-disable-next-line react/no-array-index-key */
          key={i}
          href={p.value}
          className="pc-link"
          target="_blank"
          rel="noreferrer"
          onClick={(e) => maybeHandleLink(e, p.value, onLinkClick)}
        >
          {p.value}
        </a>
      ) : (
        /* eslint-disable-next-line react/no-array-index-key */
        <React.Fragment key={i}>{p.value}</React.Fragment>
      )))}
    </>
  );
}

/* ------------------------------------------------------------- MediaGroup */
// Grouped media, clustered iMessage / WhatsApp style. View-only (no upload).
// Handles two real-world cases gracefully:
//   (a) a broken / unavailable photo or video → neutral fallback tile
//   (b) a video → the browser renders the first frame as the thumbnail (no stored
//       poster exists), with a play affordance + a duration read from its metadata.

// mm:ss from a number of seconds (e.g. 75 -> "1:15").
function formatDuration(seconds: number): string {
  const total = Math.round(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

function MediaTile({
  item, overflow, onOpen,
}: {
  item: TimelineMedia;
  overflow: number;
  onOpen?: (item: TimelineMedia) => void;
}) {
  const [imgErrored, setImgErrored] = useState(false);
  const [videoErrored, setVideoErrored] = useState(false);
  // Duration derived from the video's own metadata (no poster/duration is stored
  // server-side); an explicit item.duration, if ever provided, takes precedence.
  const [autoDuration, setAutoDuration] = useState<string | null>(null);
  const isVideo = item.type === 'video';
  const broken = !!item.broken || (isVideo ? videoErrored : imgErrored);
  const clickable = !!onOpen && !broken;
  const open = () => { if (clickable && onOpen) onOpen(item); };
  const tileProps: React.HTMLAttributes<HTMLDivElement> = clickable
    ? {
      onClick: open,
      role: 'button',
      tabIndex: 0,
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
      },
    }
    : {};

  // Broken / unavailable — explicit flag or a runtime load error. Never a busted tag.
  if (broken) {
    const label = isVideo ? 'Video unavailable' : 'Image unavailable';
    return (
      <div className="pc-media__tile pc-media__tile--broken" role="img" aria-label={label}>
        <Icon name="broken_image" size={26} />
        <span className="pc-media__brokenlabel">{label}</span>
      </div>
    );
  }

  // (b) Video — first frame as the still (#t=0.1), play affordance + duration. Tapping
  // opens the player overlay (handled by the host via onOpen).
  if (isVideo) {
    const durationLabel = item.duration || autoDuration;
    return (
      <div className={`pc-media__tile pc-media__tile--video${clickable ? ' pc-media__tile--clickable' : ''}`} {...tileProps}>
        <video
          className="pc-media__media"
          src={`${item.src}#t=0.1`}
          preload="metadata"
          muted
          playsInline
          tabIndex={-1}
          onLoadedMetadata={(e) => {
            const d = e.currentTarget.duration;
            if (!item.duration && Number.isFinite(d) && d > 0) setAutoDuration(formatDuration(d));
          }}
          onError={() => setVideoErrored(true)}
        />
        <span className="pc-media__play" aria-hidden="true">
          <Icon name="play_arrow" size={30} />
        </span>
        {durationLabel && (
          <span className="pc-media__duration">
            <Icon name="videocam" size={13} />
            {durationLabel}
          </span>
        )}
        {overflow > 0 && <span className="pc-media__more">{`+${overflow}`}</span>}
      </div>
    );
  }

  return (
    <div className={`pc-media__tile${clickable ? ' pc-media__tile--clickable' : ''}`} {...tileProps}>
      <img src={item.src} alt={item.alt || ''} loading="lazy" onError={() => setImgErrored(true)} />
      {overflow > 0 && <span className="pc-media__more">{`+${overflow}`}</span>}
    </div>
  );
}

function MediaGroup({ media = [], onOpen }: { media?: TimelineMedia[]; onOpen?: (item: TimelineMedia) => void }) {
  if (!media.length) return null;
  const n = media.length;
  const LAYOUTS = ['', 'one', 'two', 'three'];
  const layout = n <= 3 ? LAYOUTS[n] : 'grid';
  const shown = n >= 5 ? media.slice(0, 4) : media;
  const hasVideo = media.some((m) => m.type === 'video');
  const label = hasVideo ? `${n} photo${n > 1 ? 's' : ''} and video` : `${n} photo${n > 1 ? 's' : ''}`;
  return (
    <div className={`pc-media pc-media--${layout}`} role="group" aria-label={label}>
      {shown.map((item, i) => {
        const overflow = n >= 5 && i === 3 ? n - 4 : 0;
        /* eslint-disable-next-line react/no-array-index-key */
        return <MediaTile key={i} item={item} overflow={overflow} onOpen={onOpen} />;
      })}
    </div>
  );
}

/* ------------------------------------------------------------ MediaLightbox */
// Fullscreen-within-the-thread viewer for a photo or video. Stays inside the
// thread container (position: absolute, not fixed) so it works in partner
// embeds. Scrim click, Close button, and Esc all dismiss it.
function MediaLightbox({
  media, index, date, onClose,
}: {
  media: TimelineMedia[];
  index: number;
  date: string;
  onClose: () => void;
}) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [cap, setCap] = useState<{ w: number; h: number } | null>(null);
  // Which item is shown; the lightbox is a carousel over the message's media.
  const [cur, setCur] = useState(index);
  const total = media.length;
  const item = media[cur] || media[0];
  const hasPrev = cur > 0;
  const hasNext = cur < total - 1;
  const go = useCallback(
    (delta: number) => setCur((c) => Math.min(total - 1, Math.max(0, c + delta))),
    [total],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') go(-1);
      else if (e.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, go]);

  // Cap the media to a definite length derived from the overlay (a definite
  // ancestor). A percentage max-height on the <img> won't clamp against a
  // flex-resolved parent height, so we measure instead and recompute on resize.
  const PAD = 32; const BAR = 45; const GAP = 16;
  useLayoutEffect(() => {
    const measure = () => {
      const el = overlayRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setCap({
        w: Math.max(120, Math.round(r.width - PAD * 2)),
        h: Math.max(120, Math.round(r.height - PAD * 2 - BAR - GAP)),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const isVideo = item.type === 'video';
  const mediaStyle: React.CSSProperties = cap
    ? { maxWidth: `${cap.w}px`, maxHeight: `${cap.h}px` }
    : { visibility: 'hidden' };
  return (
    <div className="pc-lightbox" role="dialog" aria-modal="true" aria-label={isVideo ? 'Video viewer' : 'Photo viewer'} ref={overlayRef}>
      <button type="button" className="pc-lightbox__scrim" aria-label="Close" onClick={onClose} />
      {hasPrev && (
        <button type="button" className="pc-lightbox__nav pc-lightbox__nav--prev" aria-label="Previous" onClick={() => go(-1)}>
          <Icon name="chevron_left" size={28} />
        </button>
      )}
      <figure className="pc-lightbox__frame">
        <div className={`pc-lightbox__media${isVideo ? ' pc-lightbox__media--video' : ''}`}>
          {isVideo ? (
            /* eslint-disable-next-line jsx-a11y/media-has-caption */
            <video key={item.src} src={item.src} style={mediaStyle} controls autoPlay playsInline />
          ) : (
            <img key={item.src} src={item.src} alt={item.alt || ''} style={mediaStyle} />
          )}
        </div>
        <figcaption className="pc-lightbox__bar">
          <button type="button" className="pc-lightbox__close" onClick={onClose}>Close</button>
          <span className="pc-lightbox__date">
            {date}
            {total > 1 ? ` · ${cur + 1} of ${total}` : ''}
          </span>
        </figcaption>
      </figure>
      {hasNext && (
        <button type="button" className="pc-lightbox__nav pc-lightbox__nav--next" aria-label="Next" onClick={() => go(1)}>
          <Icon name="chevron_right" size={28} />
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ MessageBubble */
function MessageBubble({
  message, onOpenMedia, onLinkClick,
}: {
  message: ThreadMessage;
  onOpenMedia?: (item: TimelineMedia, message: ThreadMessage) => void;
  onLinkClick?: LinkClickHandler;
}) {
  const out = message.sender === 'parent';
  return (
    <div className={`pc-row pc-row--${out ? 'out' : 'in'}`}>
      <div className={`pc-bubble pc-bubble--${out ? 'out' : 'in'}`}>
        {/* eslint-disable-next-line no-nested-ternary */}
        {message.html ? (
          /* Sanitized in the ETL by hub_message_helper.sanitize_display_html — a strict
             tag/attribute allowlist, http(s) hrefs only. Never render body_raw here.
             The click handler is pure delegation for the anchors inside (which are
             keyboard-interactive on their own); the stored HTML carries
             target="_blank", so a handled link must preventDefault to stay in-tab. */
          /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
          <div
            className="pc-bubble__text pc-bubble__html"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: message.html }}
            onClick={(e) => {
              const anchor = (e.target as HTMLElement).closest('a');
              if (anchor) maybeHandleLink(e, anchor.getAttribute('href') || '', onLinkClick);
            }}
          />
        ) : message.text ? (
          <div className="pc-bubble__text">
            {parseMessageBlocks(message.text).map((block, i) => (block.type === 'listItem' ? (
              /* eslint-disable-next-line react/no-array-index-key */
              <div key={i} className="pc-bubble__li" style={block.depth ? { marginLeft: `${block.depth * 1.1}em` } : undefined}>
                <span className="pc-bubble__limarker">{block.marker}</span>
                <span className="pc-bubble__libody"><Linkified text={block.text} onLinkClick={onLinkClick} /></span>
              </div>
            ) : (
              /* eslint-disable-next-line react/no-array-index-key */
              <div key={i} className="pc-bubble__para">
                <Linkified text={block.text} onLinkClick={onLinkClick} />
              </div>
            )))}
          </div>
        ) : null}
        {message.media ? (
          <MediaGroup media={message.media} onOpen={(it) => onOpenMedia && onOpenMedia(it, message)} />
        ) : null}
        <div className="pc-bubble__meta">
          <span className="pc-bubble__time">{message.time}</span>
          {message.channel ? (
            <>
              <span className="pc-bubble__dot" aria-hidden="true" />
              <span className="pc-bubble__channel">{message.channel}</span>
            </>
          ) : null}
          {message.survey ? (
            <>
              <span className="pc-bubble__dot pc-bubble__dot--dark" aria-hidden="true" />
              <span className="pc-bubble__survey">
                <Icon name="check_circle" size={16} className="pc-survey-check" />
                Survey completed
              </span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- DateDivider */
function DateDivider({ label }: { label: string }) {
  return (
    <div className="pc-datediv" role="separator" aria-label={label}>
      <span className="pc-datediv__pill">{label}</span>
    </div>
  );
}

/* --------------------------------------------------------- NewMessageDivider */
// In-thread marker placed before the first unread message.
const NewMessageDivider = React.forwardRef<HTMLDivElement, { count: number }>(
  ({ count }, ref) => (
    <div className="pc-newdiv" role="separator" aria-label={`${count} new messages`} ref={ref}>
      <span className="pc-newdiv__line" />
      <span className="pc-newdiv__label">{`${count} new message${count === 1 ? '' : 's'}`}</span>
      <span className="pc-newdiv__line" />
    </div>
  ),
);

/* ------------------------------------------------------------ ResponseTime */
function ResponseTime({
  variant, contactPhone, supportEmail,
}: {
  variant?: 'contact' | 'default';
  contactPhone: string;
  supportEmail: string;
}) {
  const text = variant === 'contact'
    ? `Questions? Text ${contactPhone} or email ${supportEmail} to reach a trainer.`
    : 'Trainers respond within 24 hours.';
  return <div className="pc-resptime">{text}</div>;
}

/* --------------------------------------------------------- DisabledComposer */
// Phase 1 read-only: present but non-interactive (greyed). Matches the frame's
// default composer — attach icon, placeholder field, disabled send button.
function DisabledComposer() {
  return (
    <div className="pc-composer" aria-hidden="true">
      <div className="pc-composer__row">
        <button type="button" className="pc-iconbtn pc-iconbtn--attach" tabIndex={-1} disabled>
          <Icon name="attach_file" size={24} />
        </button>
        <div className="pc-composer__field">
          <span className="pc-composer__placeholder">Write a message...</span>
        </div>
        <button type="button" className="pc-iconbtn pc-iconbtn--send" tabIndex={-1} disabled>
          <Icon name="send" size={24} />
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- UpgradePrompt */
// Inactive membership: replaces composer + response-time text. CTA is white-label
// safe (no brand name) and links to the upgrade/pricing page.
function UpgradePrompt({ upgradeUrl, onUpgradeClick }: { upgradeUrl: string; onUpgradeClick?: () => void }) {
  return (
    <div className="pc-upgrade">
      <p className="pc-upgrade__text">
        Your membership has expired. Upgrade to continue messaging with our trainers.
      </p>
      <a
        className="pc-btn pc-btn--primary"
        href={upgradeUrl}
        target="_blank"
        rel="noreferrer"
        onClick={onUpgradeClick}
        style={{ textDecoration: 'none', display: 'inline-block' }}
      >
        Upgrade Membership
      </a>
    </div>
  );
}

/* --------------------------------------------------------------- EmptyState */
// Shown when the thread has no messages. Gentle + explanatory, never blank.
// Copy adapts to membership: active members are told replies will land here;
// inactive members are nudged toward upgrading.
function EmptyState({
  membershipStatus = 'active', brandName = 'Petcademy',
}: {
  membershipStatus?: MembershipStatus;
  brandName?: string;
}) {
  const inactive = membershipStatus === 'inactive';
  const who = brandName ? `the ${brandName} team` : 'our team';
  const body = inactive
    ? 'You don’t have any messages yet. Upgrade your membership to start a conversation with our trainers.'
    : 'We’re here with resources and support for you and your pet — anytime you need us. '
      + `New messages from your trainer or ${who} will show up right here.`;
  return (
    <div className="pc-empty">
      <div className="pc-empty__icon" aria-hidden="true">
        <Icon name="sms" size={36} />
      </div>
      <h2 className="pc-empty__title">No messages yet</h2>
      <p className="pc-empty__body">{body}</p>
    </div>
  );
}

/* ------------------------------------------------------------- MessageThread */
type RenderItem =
  | { type: 'date'; label: string; key: string }
  | { type: 'newdiv'; count: number; key: string }
  | { type: 'msg'; message: ThreadMessage; key: string };

export interface MessageThreadProps {
  messages?: ThreadMessage[];
  membershipStatus?: MembershipStatus; // 'active' | 'inactive'
  unreadCount?: number;
  className?: string; // partners pass a theme class here to override --pc-* tokens
  brandName?: string; // pass "" for a white-labeled partner embed
  scrollToMessageId?: string | null; // open positioned at this message
  readOnly?: boolean; // hide the composer row, leaving only the contact note
  contactPhone?: string; // drives the read-only contact note (AWO's number)
  supportEmail?: string;
  upgradeUrl?: string; // inactive-membership CTA target
  onUpgradeClick?: () => void; // host hook fired when the upgrade CTA is clicked (analytics)
  onMarkRead?: () => void; // fired when the new-message divider enters the viewport
  // Live composer (Phase 2). When provided (and not readOnly/inactive), the
  // interactive composer replaces the read-only placeholder; the host owns the
  // network call and resolves/rejects to drive the composer's clear/error state.
  onSend?: (payload: ComposerSendPayload) => Promise<void>;
  sendError?: string | null; // surfaced beneath the composer
  // Host hook for links inside message bubbles (linkified text and sanitized
  // HTML alike). Return true to claim the link — the thread prevents the default
  // new-tab open and the host owns what happens (e.g. an in-app navigation to
  // the lesson a trainer linked). Plain left-clicks only; modified clicks keep
  // the browser default. See LinkClickHandler.
  onLinkClick?: LinkClickHandler;
  // Optional node rendered at the top of the scroll region — it scrolls away with
  // the messages, while the footer (contact note / composer) stays pinned at the
  // bottom. The Hub passes its page header here.
  header?: React.ReactNode;
  // Partner embed: replaces the upgrade CTA / contact note when the thread is
  // inactive or read-only. The host renders its own upgrade button here.
  lockedFooter?: React.ReactNode;
  // Inline style on the .pc-thread root — theme tokens must sit on the root itself.
  style?: React.CSSProperties;
  // Optional analytics hooks (embed). Absent in the Hub.
  onComposerStart?: () => void; // first focus/typing in the composer, once per mount
  onAttachmentAdded?: (kind: 'image' | 'video') => void; // once per file actually attached
  // Once per mount: the first time a message that sat entirely above the first
  // screen (the position the thread opened at) scrolls into view.
  onOlderMessagesViewed?: () => void;
}

function MessageThread({
  messages = [],
  membershipStatus = 'active',
  unreadCount,
  className = '',
  brandName = 'Petcademy',
  scrollToMessageId = null,
  readOnly = false,
  contactPhone = '(555) 555-5555',
  supportEmail = 'support@petcademy.org',
  upgradeUrl = 'https://petcademy.org/pricing/',
  onUpgradeClick,
  onMarkRead,
  header,
  onSend,
  sendError = null,
  onLinkClick,
  lockedFooter,
  style,
  onComposerStart,
  onAttachmentAdded,
  onOlderMessagesViewed,
}: MessageThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const newDivRef = useRef<HTMLDivElement>(null);
  // scrollTop that positionInitial last set. Late re-anchors (lazy image loads,
  // settle timers) are skipped once the user scrolls away from this position.
  const lastAnchoredTop = useRef<number | null>(null);
  // The scrollTop of the thread's FIRST positioning, captured once; later re-anchors
  // never move it. Analytics treats "above the first screen" relative to this.
  const firstAnchorTop = useRef<number | null>(null);
  const [lightbox, setLightbox] = useState<{ media: TimelineMedia[]; index: number; date: string } | null>(null);

  // Drag-and-drop: a thread-wide drop zone that routes files into the composer.
  const composerApi = useRef<ComposerApi | null>(null);
  const dragDepth = useRef(0);
  const [dragging, setDragging] = useState(false);

  const inactive = membershipStatus === 'inactive';
  // The interactive composer shows only when the host wired a send handler and
  // the thread isn't read-only or for an inactive member.
  const composerLive = !inactive && !readOnly && !!onSend;
  const computedUnread = typeof unreadCount === 'number'
    ? unreadCount
    : messages.filter((m) => m.unread).length;

  // Build a render list with date dividers + the single new-message divider.
  const items: RenderItem[] = [];
  let lastDay: string | null = null;
  let unreadShown = false;
  messages.forEach((m) => {
    const isNewDay = !!m.day && m.day !== lastDay;
    const isFirstUnread = !inactive && !!m.unread && !unreadShown && computedUnread > 0;
    // If the first unread also starts a new date, the "new messages" divider goes
    // ABOVE the date header; otherwise it sits directly above the first unread message
    // (the date header for that day was already emitted by an earlier message).
    if (isFirstUnread && isNewDay) {
      items.push({ type: 'newdiv', count: computedUnread, key: 'newdiv' });
      unreadShown = true;
      items.push({ type: 'date', label: m.day as string, key: `d-${m.day}` });
      lastDay = m.day as string;
    } else {
      if (isNewDay) {
        items.push({ type: 'date', label: m.day as string, key: `d-${m.day}` });
        lastDay = m.day as string;
      }
      if (isFirstUnread) {
        items.push({ type: 'newdiv', count: computedUnread, key: 'newdiv' });
        unreadShown = true;
      }
    }
    items.push({ type: 'msg', message: m, key: m.id });
  });

  // Initial positioning within the internal scroll region: scrollToMessageId -> that
  // message; active with unread -> the "new messages" divider (near the top);
  // otherwise -> the end of the thread.
  const positionInitial = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    if (scrollToMessageId) {
      const target = sc.querySelector<HTMLElement>(`[data-mid="${scrollToMessageId}"]`);
      if (target) {
        sc.scrollTop = Math.max(0, target.offsetTop - 56);
        lastAnchoredTop.current = sc.scrollTop;
        if (firstAnchorTop.current === null) firstAnchorTop.current = sc.scrollTop;
        return;
      }
    }
    if (inactive) {
      sc.scrollTop = sc.scrollHeight;
    } else if (newDivRef.current && computedUnread > 0) {
      // Open positioned at the "new messages" divider, near the top.
      sc.scrollTop = Math.max(0, newDivRef.current.offsetTop - 16);
    } else {
      sc.scrollTop = sc.scrollHeight;
    }
    lastAnchoredTop.current = sc.scrollTop;
    if (firstAnchorTop.current === null) firstAnchorTop.current = sc.scrollTop;
  }, [inactive, computedUnread, scrollToMessageId]);

  useLayoutEffect(() => {
    positionInitial();
    // Re-run after async content (photos) loads and changes scroll height, so the
    // anchor lands correctly even before images are decoded (real prototype bug).
    // Only while the user hasn't scrolled away: message photos are loading="lazy",
    // so images above the viewport fire `load` as the user scrolls UP through
    // history — unconditionally re-anchoring here yanked them back to the bottom.
    const sc = scrollRef.current;
    if (!sc) return undefined;
    const reanchorIfUnmoved = () => {
      const el = scrollRef.current;
      if (!el) return;
      const anchored = lastAnchoredTop.current;
      if (anchored !== null && Math.abs(el.scrollTop - anchored) > 8) return;
      positionInitial();
    };
    const imgs = Array.from(sc.querySelectorAll('img'));
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', reanchorIfUnmoved); });
    const t1 = setTimeout(reanchorIfUnmoved, 150);
    const t2 = setTimeout(reanchorIfUnmoved, 600);
    return () => {
      imgs.forEach((img) => img.removeEventListener('load', reanchorIfUnmoved));
      clearTimeout(t1); clearTimeout(t2);
    };
  }, [membershipStatus, scrollToMessageId, positionInitial]);

  // Analytics: first time a message that was entirely above the opened-at
  // position scrolls into view. Passive, only attached when the prop is set.
  const olderViewedRef = useRef(onOlderMessagesViewed);
  olderViewedRef.current = onOlderMessagesViewed;
  const hasOlderHook = !!onOlderMessagesViewed;
  useEffect(() => {
    const sc = scrollRef.current;
    if (!hasOlderHook || !sc) return undefined;
    const onScroll = () => {
      const anchor = firstAnchorTop.current;
      if (anchor === null) return;
      let boundary = -1;
      sc.querySelectorAll<HTMLElement>('[data-mid]').forEach((el) => {
        const bottom = el.offsetTop + el.offsetHeight;
        if (bottom <= anchor && bottom > boundary) boundary = bottom;
      });
      if (boundary < 0 || sc.scrollTop >= boundary) return;
      sc.removeEventListener('scroll', onScroll);
      if (olderViewedRef.current) olderViewedRef.current();
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => sc.removeEventListener('scroll', onScroll);
  }, [hasOlderHook]);

  // Mark-as-read once the new-message divider scrolls into view. Persistence is
  // owned by the host (onMarkRead); this only reports the "seen" signal.
  useEffect(() => {
    const el = newDivRef.current;
    const root = scrollRef.current;
    if (!el || !root || !onMarkRead || inactive || computedUnread <= 0) return undefined;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { onMarkRead(); obs.disconnect(); }
      });
    }, { root, threshold: 0.6 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [onMarkRead, inactive, computedUnread]);

  // When a message is appended (the user sends one, or a reply arrives), glide to
  // the bottom so the new message is fully visible above the pinned composer —
  // otherwise it lands below the fold / behind the footer. Re-pin as late layout
  // settles (the cache-and-network refetch re-renders and lazy images load and
  // grow the content after the first scroll).
  const prevMsgCount = useRef(messages.length);
  useEffect(() => {
    if (messages.length <= prevMsgCount.current) {
      prevMsgCount.current = messages.length;
      return undefined;
    }
    prevMsgCount.current = messages.length;
    const sc = scrollRef.current;
    if (!sc) return undefined;
    const toBottom = (behavior: ScrollBehavior) => sc.scrollTo({ top: sc.scrollHeight, behavior });
    toBottom('smooth');
    const imgs = Array.from(sc.querySelectorAll('img'));
    const repin = () => toBottom('auto');
    imgs.forEach((img) => { if (!img.complete) img.addEventListener('load', repin); });
    const t1 = setTimeout(repin, 200);
    const t2 = setTimeout(repin, 700);
    return () => {
      imgs.forEach((img) => img.removeEventListener('load', repin));
      clearTimeout(t1); clearTimeout(t2);
    };
  }, [messages.length]);

  // Drag-and-drop: route dropped image/video files into the composer tray.
  const hasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer?.types || []).indexOf('Files') !== -1;
  const onDragEnter = (e: React.DragEvent) => {
    if (!composerLive || !hasFiles(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setDragging(true);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!composerLive || !dragging) return;
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
  };
  const onDragLeave = () => {
    if (!composerLive) return;
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) { dragDepth.current = 0; setDragging(false); }
  };
  const onDrop = (e: React.DragEvent) => {
    if (!composerLive) return;
    e.preventDefault();
    dragDepth.current = 0;
    setDragging(false);
    const files = e.dataTransfer?.files;
    if (files && files.length && composerApi.current) composerApi.current.addFiles(files);
  };

  return (
    <div
      className={`pc-thread${className ? ` ${className}` : ''}`}
      style={style}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="pc-thread__scroll" ref={scrollRef}>
        {header}
        {messages.length === 0 ? (
          <EmptyState membershipStatus={membershipStatus} brandName={brandName} />
        ) : (
          <div className="pc-thread__list">
            {items.map((it) => {
              if (it.type === 'date') return <DateDivider key={it.key} label={it.label} />;
              if (it.type === 'newdiv') return <NewMessageDivider key={it.key} count={it.count} ref={newDivRef} />;
              return (
                <div key={it.key} data-mid={it.message.id}>
                  <MessageBubble
                    message={it.message}
                    onLinkClick={onLinkClick}
                    onOpenMedia={(item, msg) => setLightbox({
                      media: msg.media || [],
                      index: Math.max(0, (msg.media || []).indexOf(item)),
                      date: msg.time,
                    })}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="pc-thread__footer">
        {lockedFooter && (inactive || readOnly) ? lockedFooter : (
          <>
            {inactive && <UpgradePrompt upgradeUrl={upgradeUrl} onUpgradeClick={onUpgradeClick} />}
            {!inactive && readOnly && (
              <ResponseTime variant="contact" contactPhone={contactPhone} supportEmail={supportEmail} />
            )}
            {!inactive && !readOnly && (
              <>
                {onSend ? (
                  <Composer
                    onSend={onSend}
                    onReady={(api) => { composerApi.current = api; }}
                    sendError={sendError}
                    onStart={onComposerStart}
                    onAttachmentAdded={onAttachmentAdded}
                  />
                ) : (
                  <DisabledComposer />
                )}
                <ResponseTime contactPhone={contactPhone} supportEmail={supportEmail} />
              </>
            )}
          </>
        )}
      </div>

      {dragging && composerLive && (
        <div className="pc-dropzone" aria-hidden="true">
          <div className="pc-dropzone__card">
            <span className="pc-dropzone__icon"><Icon name="add_photo" size={26} /></span>
            <span className="pc-dropzone__title">Drop to attach</span>
            <span className="pc-dropzone__sub">Photos &amp; videos</span>
          </div>
        </div>
      )}

      {lightbox && (
        <MediaLightbox
          media={lightbox.media}
          index={lightbox.index}
          date={lightbox.date}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

export default MessageThread;
